import { useQuery } from '@pinia/colada'

import axios from 'axios'

import { useIntervalFn } from '@vueuse/core'

import { computed, watch } from 'vue'

import useOuraAuth from '#web-oura-tools/auth'

import { useAxios } from '#vue-shared-lib/composables/axios'

import type { OcpStatusResponse, OcpWhoamiResponse } from '#ourahub-intra/types/ocp'

const ouraAuth = useOuraAuth()

const { axiosClient } = useAxios({
  agent: 'ourahub-intra',
  url: import.meta.env.VITE_OCP_API_URL,
  version: import.meta.env.VITE_APP_VERSION,
})

const queryKeysOcp = {
  whoami: (uid: string, env: string) => ['ocp', 'whoami', uid, env] as const,
  status: (uid: string, env: string, service: string) => ['ocp', 'status', uid, env, service] as const,
}

// The OCP API sits behind Twingate on the management clusters, so a failure here is
// usually the network rather than auth. Errors surface on the query instead of being
// swallowed, so callers can tell the two apart.

export const useOcpWhoamiQuery = () =>
  useQuery({
    enabled: () => !!ouraAuth.user.value,
    key: () => queryKeysOcp.whoami(ouraAuth.user.value?.id ?? '', ouraAuth.appEnv.value),
    query: async (): Promise<OcpWhoamiResponse> => {
      const { data } = await axiosClient.get<OcpWhoamiResponse>('/whoami')

      return data
    },
  })

export const useOcpStatusQuery = (service: string, poll = false) => {
  const enabled = computed(() => !!ouraAuth.user.value && !service.startsWith('outo-'))

  const query = useQuery({
    enabled,
    key: () => queryKeysOcp.status(ouraAuth.user.value?.id ?? '', ouraAuth.appEnv.value, service),
    query: async (): Promise<OcpStatusResponse> => {
      const { data } = await axiosClient.get<OcpStatusResponse>(`/status/${service}`)

      return data
    },
  })

  if (poll) {
    const { pause, resume } = useIntervalFn(
      () => {
        if (!query.isLoading.value) void query.refetch()
      },
      () => ocpStatusPollInterval(query.data.value),
      { immediate: false },
    )

    watch(enabled, (value) => (value ? resume() : pause()), { immediate: true })
  }

  return query
}

/** Retry unhealthy apps every minute; otherwise check every fifteen minutes. */
export function ocpStatusPollInterval(status: OcpStatusResponse | undefined): number {
  return isOcpAppNotRunning(status) ? 60_000 : 900_000
}

/** Missing app or workload status is unknown, not evidence that the app is down. */
export function isOcpAppNotRunning(status: OcpStatusResponse | undefined): boolean {
  const appStatus = status?.resources?.find((resource) => resource.kind === 'OuraHubApp')

  return appStatus?.workloads?.some((workload) => workload.ok === false) ?? false
}

/** A short reason for a failed OCP API call, telling the network apart from auth. */
export function describeOcpError(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : String(error)
  }

  const status = error.response?.status

  if (status === undefined) return 'Unreachable, check Twingate'
  if (status === 401) return 'Sign-in token rejected'
  if (status === 403) return 'Account not allowed'

  return `HTTP ${status}`
}
